import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { ArrowDropDown, Delete } from '@mui/icons-material';
import { TimeFieldInput, transformTimeValue, } from 'console/shared/components/fields';
import { useRefAnchor } from 'shared/hooks/utils';
import { DaysPopup } from './days-popup';
import { TimeLine } from './time-line/time-line';
import { TemperatureInput } from './temperature-input';
import { applyTimeChanges, changeIntervals } from './time-line/transform';
import { useConfirmDialog } from 'shared/hooks/ui/confirm-dialog';
import { interimIntervalChanges } from 'console/schedule-editor/time-line/interim-Interval-changes';
import { debounce } from 'throttle-debounce';
import { ControlWithoutValidation } from 'console/shared/components/fields/control/without-validation';
import { useNavigateBack } from 'shared/hooks/router';
import { intervalTimeSum } from './utils';
export const DayScheduleElement = memo(function DayScheduleElement(props) {
    const { day, heating_modes, temp_or_mode, selected_hc, onChangeSingleDay, onChangeDays, is_boiler_zone, is_baxi_app, } = props;
    const { day_intervals } = day;
    const [anchorEl, anchorRef, onOpenCopyMenu, onCloseCopyMenu] = useRefAnchor();
    const [ConfirmDialog, _openConfirmDialog] = useConfirmDialog();
    const [local_intervals, setLocalIntervals] = useState(day_intervals);
    const goBack = useNavigateBack();
    useEffect(() => {
        setLocalIntervals(day_intervals);
    }, [day_intervals]);
    const [selected_interval_id, setSelectedIntervalId] = useState(null);
    const [selected_interval, setSelectedInterval] = useState(null);
    const [start_time, setStartTime] = useState({ hour: 0, min: 0 });
    const [end_time, setEndTime] = useState({ hour: 0, min: 0 });
    const [tempOrMode, setTempOrMode] = useState(temp_or_mode);
    const applySelectInterval = useCallback((interval) => {
        if (interval != null) {
            const { start_hour, start_min, end_hour, end_min } = interval;
            setStartTime({ hour: start_hour, min: start_min });
            setEndTime({ hour: end_hour, min: end_min });
            setTempOrMode('temp_or_mode' in interval
                ? interval.temp_or_mode
                : props.temp_or_mode);
            setSelectedInterval(interval);
        }
    }, [
        setStartTime,
        setEndTime,
        setTempOrMode,
        setSelectedInterval,
        props.temp_or_mode,
    ]);
    const onSetNewId = useCallback((new_id) => {
        setSelectedIntervalId(new_id);
        if (new_id != null) {
            applySelectInterval(local_intervals[new_id]);
        }
    }, [setSelectedIntervalId, applySelectInterval, local_intervals]);
    const setChangesToDay = useCallback((intervals, selected_id) => {
        const [new_id, result] = interimIntervalChanges(intervals, selected_id, {});
        onChangeSingleDay({
            ...day,
            day_intervals: result,
        });
        if (new_id != null) {
            const _selected_interval = result[new_id];
            applySelectInterval(_selected_interval);
        }
        setSelectedIntervalId(new_id);
    }, [onChangeSingleDay, day, setSelectedIntervalId, applySelectInterval]);
    const onBlur = useCallback(() => {
        const [new_id, new_intervals] = applyTimeChanges(local_intervals, selected_interval_id);
        setChangesToDay(new_intervals, new_id);
    }, [applyTimeChanges, setChangesToDay, local_intervals, selected_interval_id]);
    const onChangeStartTime = useCallback((times) => {
        if (intervalTimeSum(times.hour, times.min) <
            intervalTimeSum(end_time.hour, end_time.min)) {
            setStartTime(times);
            setLocalIntervals((intervals) => {
                const [new_id, result] = interimIntervalChanges(intervals, selected_interval_id, {
                    start_hour: times.hour,
                    start_min: times.min,
                });
                setSelectedIntervalId(new_id);
                return result;
            });
        }
    }, [setStartTime, selected_interval_id, setLocalIntervals, setSelectedIntervalId]);
    const onChangeStartTimeDebounce = useMemo(() => debounce(500, onChangeStartTime), [onChangeStartTime]);
    const onChangeEndTime = useCallback((times) => {
        if (intervalTimeSum(times.hour, times.min) >
            intervalTimeSum(start_time.hour, start_time.min)) {
            setEndTime(times);
            setLocalIntervals((intervals) => {
                const [new_id, result] = interimIntervalChanges(intervals, selected_interval_id, {
                    end_hour: times.hour,
                    end_min: times.min,
                });
                setSelectedIntervalId(new_id);
                return result;
            });
        }
    }, [setEndTime, selected_interval_id, setLocalIntervals, setSelectedIntervalId]);
    const onChangeEndTimeDebounce = useMemo(() => debounce(500, onChangeEndTime), [onChangeEndTime]);
    const onChangeTempOrMode = useCallback((temp_or_mode) => {
        setTempOrMode(temp_or_mode);
        const new_intervals = changeIntervals(local_intervals, selected_interval_id, {
            temp_or_mode,
        });
        setChangesToDay(new_intervals, selected_interval_id);
    }, [setTempOrMode, local_intervals, selected_interval_id, setChangesToDay]);
    const onDeleteInterval = useCallback(() => {
        const new_intervals = local_intervals.filter((_item, index) => index !== selected_interval_id);
        setChangesToDay(new_intervals, selected_interval_id);
    }, [local_intervals, selected_interval_id, setChangesToDay]);
    const [temp_interval, setTempInterval] = useState(null);
    const openConfirmDialog = useCallback((_interval_id, interval) => {
        _openConfirmDialog();
        setTempInterval(interval);
    }, [_openConfirmDialog, setTempInterval]);
    const onCreateInterval = useCallback((interval) => {
        const id = local_intervals.findIndex((_interval) => _interval.start_hour === interval.start_hour &&
            _interval.start_min === interval.start_min);
        const new_intervals = [
            ...local_intervals,
            { ...interval, temp_or_mode: temp_or_mode },
        ];
        setChangesToDay(new_intervals, id);
    }, [local_intervals, setChangesToDay, temp_or_mode]);
    const onAcceptClick = useCallback(() => {
        if (temp_interval != null) {
            onCreateInterval(temp_interval);
        }
        goBack();
    }, [onCreateInterval, temp_interval]);
    const onDoCopy = useCallback((days) => onChangeDays(days.map((d) => ({ key: d, day_intervals: local_intervals }))), [local_intervals, onChangeDays]);
    const timeline_numbers = useMemo(() => [
        { id: 0, time: '00:00' },
        { id: 1, time: '04:00' },
        { id: 2, time: '08:00' },
        { id: 3, time: '12:00' },
        { id: 4, time: '16:00' },
        { id: 5, time: '20:00' },
        { id: 6, time: '00:00' },
    ], []);
    const timeline_marks = useMemo(() => timeline_numbers.map(({ id, time }) => (_jsxs(Box, { display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", children: [_jsx(Box, { width: "1px", height: "12px", bgcolor: "#808080" }), _jsx(Typography, { color: "textSecondary", children: time })] }, id))), [timeline_numbers]);
    const show_timeline = useMemo(() => !is_baxi_app, [is_baxi_app]);
    return (_jsxs(Box, { className: "day-schedule-editor", display: "flex", flexGrow: 1, p: 2, height: "100%", boxSizing: "border-box", children: [_jsxs(Box, { component: "div", display: "flex", width: "100%", flexDirection: is_baxi_app ? 'row' : 'column', sx: {
                    gridColumnGap: '10px',
                }, children: [_jsxs(Box, { display: "flex", flexDirection: "column", boxSizing: "border-box", height: is_baxi_app ? '100%' : '100px', width: "100%", mb: 2, children: [_jsx(TimeLine, { day_intervals: local_intervals, temp_or_mode: temp_or_mode, is_baxi_app: is_baxi_app, heating_modes: heating_modes, selected_interval_id: selected_interval_id, onSelectInterval: onSetNewId, onCreateInterval: openConfirmDialog, selected_hc: selected_hc }), show_timeline && (_jsx(Box, { component: "div", display: "flex", justifyContent: "space-between", mx: '-10px', children: timeline_marks }))] }), _jsxs(Box, { component: "div", display: "flex", alignSelf: "flex-start", flexDirection: "column", width: "100%", sx: {
                            gridRowGap: 12,
                        }, children: [_jsx(Box, { component: "div", children: _jsx(ControlWithoutValidation, { label: "\u041D\u0430\u0447\u0430\u043B\u043E \u0438\u043D\u0442\u0435\u0440\u0432\u0430\u043B\u0430", value: start_time, onChange: onChangeStartTimeDebounce, field: (input_props) => (_jsx(TimeFieldInput, { disabled: selected_interval_id == null, inputProps: {
                                            min: '00:00',
                                            max: transformTimeValue({
                                                hour: selected_interval?.end_hour ?? 23,
                                                min: selected_interval?.end_min ?? 59,
                                            }),
                                        }, onBlur: onBlur, ...input_props })) }) }), _jsx(Box, { component: "div", children: _jsx(ControlWithoutValidation, { label: "\u041A\u043E\u043D\u0435\u0446 \u0438\u043D\u0442\u0435\u0440\u0432\u0430\u043B\u0430", value: end_time, onChange: onChangeEndTimeDebounce, field: (input_props) => (_jsx(TimeFieldInput, { disabled: selected_interval_id == null, inputProps: {
                                            min: transformTimeValue({
                                                hour: selected_interval?.start_hour ?? 0,
                                                min: selected_interval?.start_min ?? 0,
                                            }),
                                            max: '23:59',
                                        }, onBlur: onBlur, ...input_props })) }) }), _jsx(Box, { children: _jsx(TemperatureInput, { heating_modes: heating_modes, temp_or_mode: tempOrMode, onChange: onChangeTempOrMode, disabled: selected_interval_id == null, is_boiler_mode: is_boiler_zone }) }), _jsx(Box, { children: _jsx(Box, { display: "flex", justifyContent: "flex-end", children: _jsx(Button, { variant: "outlined", onClick: onDeleteInterval, startIcon: _jsx(Delete, {}), disabled: selected_interval_id == null, children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" }) }) }), selected_interval_id == null && (_jsx(Box, { children: _jsx(Typography, { variant: "body2", color: "textSecondary", children: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0438\u043D\u0442\u0435\u0440\u0432\u0430\u043B \u0434\u043B\u044F \u0440\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F" }) })), _jsx(Box, { children: _jsx(Divider, {}) }), _jsx(Box, { children: _jsx(Box, { display: "flex", justifyContent: "flex-end", children: _jsx(Button, { color: "secondary", variant: "outlined", endIcon: _jsx(ArrowDropDown, {}), ref: anchorRef, onClick: onOpenCopyMenu, children: "\u041A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C" }) }) })] })] }), _jsx(ConfirmDialog, { onConfirm: onAcceptClick, onReject: goBack, children: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0438\u043D\u0442\u0435\u0440\u0432\u0430\u043B?" }), _jsx(DaysPopup, { anchorEl: anchorEl, onClose: onCloseCopyMenu, current_day: day.key, doCopy: onDoCopy })] }));
});
